import React, { useEffect, useRef } from 'react';

interface ModalProps {
    isOpen: boolean;
    children: React.ReactNode;
    /** Tailwind width class for the card, e.g. "max-w-md" (default), "max-w-lg", "max-w-2xl". */
    maxWidthClassName?: string;
    /** Extra classes for the card itself (bg, rounding, shadow, overflow, max-height, animations...). */
    cardClassName?: string;
    /** Extra classes for the backdrop (opacity, blur). */
    overlayClassName?: string;
    /** z-index class for the overlay. Defaults to z-50; bump for modals that must stack above others. */
    zIndexClassName?: string;
    /** Fecha com Esc. Sem ele, o Esc não faz nada (modais que não podem ser fechados assim). */
    onClose?: () => void;
    /** Nome do diálogo para leitores de tela, quando o título visível não basta. */
    ariaLabel?: string;
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DEFAULT_CARD_CLASSES = 'bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full overflow-hidden';
const DEFAULT_OVERLAY_CLASSES = 'p-4 bg-black/50 backdrop-blur-sm';

/**
 * Shared overlay + card shell used across the app's modals, extracted because ~20 modal
 * components repeated the same `fixed inset-0 ... flex items-center justify-center` wrapper.
 * Only the shell is shared — header/body/footer content stays in each modal, since that's
 * where the real (non-duplicated) logic lives.
 */
export function Modal({
    isOpen,
    children,
    maxWidthClassName = 'max-w-md',
    cardClassName = DEFAULT_CARD_CLASSES,
    overlayClassName = DEFAULT_OVERLAY_CLASSES,
    zIndexClassName = 'z-50',
    onClose,
    ariaLabel,
}: ModalProps) {
    const cardRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    // Acessibilidade: foco entra no diálogo ao abrir, Tab não escapa para a página de trás,
    // Esc fecha (quando há onClose) e o foco volta para onde estava ao fechar.
    useEffect(() => {
        if (!isOpen) return;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        const card = cardRef.current;
        if (card && !card.contains(document.activeElement)) {
            const first = card.querySelector<HTMLElement>('[autofocus]') ?? card;
            first.focus({ preventScroll: true });
        }

        const onKeyDown = (e: KeyboardEvent) => {
            if (!cardRef.current) return;
            if (e.key === 'Escape' && onCloseRef.current) {
                e.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (e.key !== 'Tab') return;
            const focusables = Array.from(cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE))
                .filter(el => el.offsetParent !== null || el === document.activeElement);
            if (focusables.length === 0) { e.preventDefault(); return; }
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && (document.activeElement === first || document.activeElement === cardRef.current)) {
                e.preventDefault(); last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault(); first.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            previouslyFocused?.focus?.({ preventScroll: true });
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={`fixed inset-0 ${zIndexClassName} flex items-center justify-center ${overlayClassName}`}>
            <div
                ref={cardRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                tabIndex={-1}
                className={`${cardClassName} ${maxWidthClassName} outline-none`}
            >
                {children}
            </div>
        </div>
    );
}
